import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '@/lib/api';
import { ArrowLeft, Send } from 'lucide-react';
import ForumEditor from '@/components/ForumEditor';

const CATEGORIES = [
  { id: 'tiles', name: 'Tiles', color: '#56dfb4' },
  { id: 'accessories', name: 'Accessories', color: '#56c6df' },
  { id: 'nmjl', name: 'NMJL Card Questions', color: '#df5681' },
  { id: 'other', name: 'Other Mahjong Questions', color: '#b456df' },
];
const TAG_SUGGESTIONS = ['identification', 'terminology', 'rules', 'deck-building', 'maintenance', 'gear', 'strategy', 'scoring', 'etiquette'];

export default function ForumNewThreadPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [form, setForm] = useState({
    category: searchParams.get('category') || 'tiles',
    title: '',
    body: '',
    tags: [],
  });
  const [loading, setLoading] = useState(false);
  const [tagInput, setTagInput] = useState('');

  const toggleTag = (tag) => {
    setForm(p => ({
      ...p,
      tags: p.tags.includes(tag) ? p.tags.filter(t => t !== tag) : p.tags.length < 3 ? [...p.tags, tag] : p.tags,
    }));
  };

  const addCustomTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !form.tags.includes(tag) && form.tags.length < 3) {
      setForm(p => ({ ...p, tags: [...p.tags, tag] }));
      setTagInput('');
    }
  };

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error('Title and body are required'); return; }
    setLoading(true);
    try {
      const { data } = await api.post('/forum/threads', form);
      toast.success('Thread created!');
      navigate(`/forum/thread/${data.thread_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create thread');
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="forum-new-thread-page" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/forum" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to Forum
      </Link>
      <h1 className="text-3xl font-bold mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>New Thread</h1>

      <Card className="border-slate-100">
        <CardContent className="p-8 space-y-5">
          <div>
            <Label className="text-sm font-medium mb-2 block">Category</Label>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map(c => (
                <button key={c.id} type="button" onClick={() => setForm(p => ({ ...p, category: c.id }))}
                  className={`px-4 py-2 rounded-full text-sm border transition-all ${form.category === c.id ? 'text-white border-transparent' : 'border-slate-200'}`}
                  style={form.category === c.id ? { backgroundColor: c.color } : {}}
                  data-testid={`thread-cat-${c.id}`}>
                  {c.name}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-sm mb-1.5 block">Title *</Label>
            <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="What's your question or topic?" className="rounded-xl" data-testid="thread-title" />
          </div>
          <div>
            <Label className="text-sm mb-1.5 block">Body *</Label>
            <ForumEditor value={form.body} onChange={(val) => setForm(p => ({ ...p, body: val }))} rows={8} placeholder="Provide context and ask your question..." testId="thread-body" />
          </div>
          <div>
            <Label className="text-sm font-medium mb-2 block">Tags (up to 3)</Label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {TAG_SUGGESTIONS.map(tag => (
                <button key={tag} type="button" onClick={() => toggleTag(tag)}
                  className={`px-2.5 py-1 rounded-full text-xs border transition-all ${form.tags.includes(tag) ? 'bg-[#56c6df] text-white border-[#56c6df]' : 'border-slate-200 hover:border-slate-300'}`}>
                  {tag}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <Input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCustomTag())}
                placeholder="Add custom tag..." className="rounded-xl text-sm max-w-xs" />
            </div>
            {form.tags.length > 0 && (
              <div className="flex gap-1 mt-2">
                {form.tags.map(t => (
                  <span key={t} className="px-2 py-0.5 rounded-full bg-[#56c6df]/15 text-[#0f172a] text-xs flex items-center gap-1">
                    {t} <button onClick={() => toggleTag(t)} className="text-slate-400 hover:text-red-400">&times;</button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="pt-4 border-t border-slate-100">
            <Button onClick={handleSubmit} disabled={loading} className="rounded-full bg-[#df5681] text-white hover:bg-[#c44570] gap-2 px-8" data-testid="submit-thread-btn">
              <Send className="w-4 h-4" />{loading ? 'Posting...' : 'Post Thread'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
